import { Seo } from "../components/Seo";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Lock,
  Download,
  FileText,
  BarChart3,
  Users,
  Calendar,
  ChevronRight,
  ExternalLink,
  ArrowLeft,
  CheckCircle2
} from "lucide-react";
import { Link } from "react-router-dom";
import { useGatekeeper } from "../components/Gatekeeper";
import SwarmBackground from "../components/SwarmBackground";

const investorResources = [
  {
    icon: FileText,
    title: "Pitch Deck",
    description: "Full investor presentation (30 slides)",
    type: "PDF",
    size: "4.2 MB"
  },
  {
    icon: BarChart3,
    title: "One-Pager",
    description: "Executive summary and key metrics",
    type: "PDF",
    size: "1.1 MB"
  },
  {
    icon: Users,
    title: "Team Overview",
    description: "Leadership profiles and advisors",
    type: "PDF",
    size: "2.3 MB"
  }
];

const teamMembers = [
  // Founder bio will be fetched from authenticated endpoint (Item 2)
  // Removed from public bundle per v5 spec
];

const liveMetrics = [
  { label: "Lines of Code", value: "195K+", trend: "Full-stack" },
  { label: "Automated Tests", value: "297+", trend: "Passing" },
  { label: "API Endpoints", value: "150", trend: "REST" },
  { label: "Connectors", value: "39", trend: "9 categories" }
];

export const InvestorsPage = () => {
  const { investorUnlocked, unlockInvestor } = useGatekeeper();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleUnlock = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    setTimeout(() => {
      const success = unlockInvestor(password);
      if (!success) {
        setError("Invalid investor access code.");
      }
      setIsLoading(false);
    }, 500);
  };

  // Password gate for investor portal
  if (!investorUnlocked) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center relative" data-testid="investor-gate">
        <Seo title="Investor Portal | SwarmCitadel" description="Access the SwarmCitadel investor portal." path="/investors"><meta name="robots" content="noindex" /></Seo>
        <SwarmBackground />
        
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-swarm-green/10 rounded-full blur-[120px]" />
        
        <div className="relative z-10 w-full max-w-md px-6">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="glass-card p-8 border-swarm-green/30">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-2xl bg-swarm-green/10">
                <Lock className="w-8 h-8 text-swarm-green" />
              </div>
            </div>
            
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-white mb-2">Investor Portal</h1>
              <p className="text-slate-400 text-sm">
                This area is restricted to authorized investors.
              </p>
            </div>
            
            <form onSubmit={handleUnlock} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="Enter investor access code"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-swarm-darker border-swarm-border text-white h-12"
                  data-testid="investor-password"
                />
              </div>
              
              {error && (
                <p className="text-red-400 text-sm text-center" data-testid="investor-error">
                  {error}
                </p>
              )}
              
              <Button
                type="submit"
                disabled={isLoading || !password}
                className="w-full h-12 bg-swarm-green hover:bg-swarm-green/90 text-white"
                data-testid="investor-unlock"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Access Portal <ChevronRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-swarm-border text-center">
              <p className="text-slate-500 text-xs">
                Need access?{" "}
                <a href="/contact" className="text-swarm-cyan hover:underline">
                  Contact investor relations
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Investor Portal Content
  return (
    <div className="pt-24" data-testid="investors-page">
      <Seo title="Investor Portal | SwarmCitadel" description="Access the SwarmCitadel investor portal." path="/investors"><meta name="robots" content="noindex" /></Seo>
      {/* Header */}
      <section className="py-12 md:py-16 border-b border-swarm-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="section-label text-swarm-green">Investor Portal</div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">
                Welcome, Investor
              </h1>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-swarm-green/10 border border-swarm-green/30">
              <span className="w-2 h-2 rounded-full bg-swarm-green animate-pulse" />
              <span className="text-swarm-green text-sm font-medium">Authorized Access</span>
            </div>
          </div>
        </div>
      </section>

      {/* Live Metrics */}
      <section className="py-12 bg-swarm-darker/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-xl font-semibold text-white mb-6">Live Platform Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {liveMetrics.map((metric) => (
              <div key={metric.label} className="glass-card p-6">
                <div className="text-slate-500 text-sm mb-1">{metric.label}</div>
                <div className="text-2xl font-bold text-white font-mono">{metric.value}</div>
                <div className="text-swarm-green text-xs mt-1">{metric.trend}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Opportunity / TAM */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-xl font-semibold text-white mb-8">Market Opportunity</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="glass-card p-6 border-swarm-violet/30">
              <div className="text-4xl font-bold text-swarm-violet mb-2">$93B</div>
              <div className="text-white font-medium mb-1">Enterprise AI Market</div>
              <div className="text-slate-500 text-sm">By 2032 (CAGR 37%)</div>
            </div>
            <div className="glass-card p-6 border-swarm-cyan/30">
              <div className="text-4xl font-bold text-swarm-cyan mb-2">$182B</div>
              <div className="text-white font-medium mb-1">AI Agent Platforms</div>
              <div className="text-slate-500 text-sm">By 2033</div>
            </div>
            <div className="glass-card p-6 border-swarm-green/30">
              <div className="text-4xl font-bold text-swarm-green mb-2">247K+</div>
              <div className="text-white font-medium mb-1">OpenClaw Deployments</div>
              <div className="text-slate-500 text-sm">Zero governance today</div>
            </div>
          </div>
          <p className="text-slate-400 text-sm mt-6 text-center">
            Every autonomous agent needs governance. SwarmCitadel is the institutional layer for the agent economy.
          </p>
        </div>
      </section>

      {/* Architecture Overview */}
      <section className="py-12 md:py-16 bg-swarm-darker/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-xl font-semibold text-white mb-8">Platform Architecture</h2>
          <div className="glass-card p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-swarm-violet/10 rounded-xl border border-swarm-violet/20">
                <div className="text-swarm-violet font-bold text-lg mb-2">Governance Layer</div>
                <p className="text-slate-400 text-sm">Policy engine, approval quorums, KYA, audit trail, compliance mapping</p>
              </div>
              <div className="text-center p-6 bg-swarm-cyan/10 rounded-xl border border-swarm-cyan/20">
                <div className="text-swarm-cyan font-bold text-lg mb-2">Orchestration Layer</div>
                <p className="text-slate-400 text-sm">Workflow builder, agent management, task routing, mission control</p>
              </div>
              <div className="text-center p-6 bg-swarm-green/10 rounded-xl border border-swarm-green/20">
                <div className="text-swarm-green font-bold text-lg mb-2">Treasury Layer</div>
                <p className="text-slate-400 text-sm">Budget control, revenue distribution, tap rules, CFO dashboard</p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-swarm-border text-center">
              <p className="text-slate-500 text-sm">39+ platform connectors today. Financial connectors (virtual cards, bank accounts) coming soon.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-xl font-semibold text-white mb-6">Investor Materials</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {investorResources.map((resource) => {
              const Icon = resource.icon;
              return (
                <div key={resource.title} className="glass-card p-6 group hover:border-swarm-green/30 transition-all">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-swarm-green/10">
                      <Icon className="w-6 h-6 text-swarm-green" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold">{resource.title}</h3>
                      <p className="text-slate-500 text-sm">{resource.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-swarm-border">
                    <span className="text-slate-500 text-xs">
                      {resource.type} • {resource.size}
                    </span>
                    <Button variant="ghost" size="sm" className="text-swarm-green hover:text-swarm-green/80">
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 md:py-16 bg-swarm-darker/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-xl font-semibold text-white mb-8">Leadership Team</h2>
          
          {/* Founder */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {teamMembers.map((member) => (
              <div key={member.name} className="glass-card p-8 border-swarm-violet/30">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-swarm-violet to-swarm-cyan flex items-center justify-center text-white text-3xl font-bold flex-shrink-0">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-1">{member.name}</h3>
                    <p className="text-swarm-violet font-medium mb-4">{member.role}</p>
                    <p className="text-slate-400 mb-4">{member.bio}</p>
                    <div className="space-y-2">
                      {member.background.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-swarm-green" />
                          <span className="text-slate-300">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SwarmCrew - AI Team */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="glass-card p-8 border-swarm-cyan/20">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="p-4 rounded-2xl bg-swarm-cyan/10">
                <span className="text-4xl">🤖</span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-white mb-2">SwarmCrew: AI Operations Team</h3>
                <p className="text-slate-400 mb-4">
                  We practice what we preach. Our internal operations are powered by 9 AI agents handling 
                  onboarding, DevOps, documentation, support, analytics, and more. This "meta narrative" 
                  demonstrates the platform's capabilities while reducing operational overhead.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Onboarding Agent", "CISO Agent", "DevOps Agent", "Analytics Agent", "Support Agent"].map((agent) => (
                    <span key={agent} className="px-3 py-1 bg-swarm-cyan/10 text-swarm-cyan text-xs rounded-full">
                      {agent}
                    </span>
                  ))}
                  <span className="px-3 py-1 bg-white/5 text-slate-400 text-xs rounded-full">
                    +4 more
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Data Room */}
      <section className="py-12 md:py-16 bg-swarm-darker/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="glass-card p-8 border-swarm-violet/30">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Full Data Room Access</h2>
                <p className="text-slate-400">
                  Complete due diligence materials, financials, and technical documentation.
                </p>
              </div>
              <Button className="bg-swarm-violet hover:bg-swarm-violet/90 whitespace-nowrap">
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Data Room
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Meeting Request */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="glass-card p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 rounded-lg bg-swarm-cyan/10">
                <Calendar className="w-6 h-6 text-swarm-cyan" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Schedule a Meeting</h2>
                <p className="text-slate-400">
                  Request a one-on-one with our founding team to discuss the opportunity.
                </p>
              </div>
            </div>
            <Link to="/contact">
              <Button className="bg-swarm-cyan hover:bg-swarm-cyan/90">
                Request Meeting <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 border-t border-swarm-border">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-sm">
            Questions?{" "}
            <a href="/contact" className="text-swarm-cyan hover:underline">
              Contact us
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default InvestorsPage;
